import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Clock3,
  Filter,
  Gauge,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  Vote,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags";

const getValue = (item, camelName, pascalName, fallback = null) =>
  item?.[camelName] ?? item?.[pascalName] ?? fallback;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const normalizeVote = (item) => {
  const voteUp = toNumber(getValue(item, "voteUp", "VoteUp"));
  const voteDown = toNumber(getValue(item, "voteDown", "VoteDown"));
  const totalVotes = toNumber(getValue(item, "totalVotes", "TotalVotes"), voteUp + voteDown);
  const eligibleMembers = toNumber(getValue(item, "eligibleMembers", "EligibleMembers"));
  const approvalPercent = toNumber(getValue(item, "approvalPercent", "ApprovalPercent"));
  const participationPercent = toNumber(getValue(item, "participationPercent", "ParticipationPercent"));
  const requiredApprovalVotes = toNumber(getValue(item, "requiredApprovalVotes", "RequiredApprovalVotes"));
  const choice = getValue(item, "vote", "Vote");
  const isApproved = Boolean(getValue(item, "isApproved", "IsApproved", false));
  const status = getValue(item, "campaignStatus", "CampaignStatus", "");
  const expiresOn = getValue(item, "campaignExpiration", "CampaignExpiration");
  const expired = expiresOn ? new Date(expiresOn).getTime() < Date.now() : false;
  const resultLabel = isApproved ? "Approved" : status?.toLowerCase() === "active" && !expired ? "In progress" : "Not approved";

  return {
    voteId: getValue(item, "voteId", "VoteId"),
    campaignId: getValue(item, "campaignId", "CampaignId"),
    title: getValue(item, "campaignTitle", "CampaignTitle", "Untitled campaign"),
    proposal: getValue(item, "campaignProposal", "CampaignProposal", ""),
    campaignType: getValue(item, "campaignType", "CampaignType", "Campaign"),
    status,
    groupId: getValue(item, "groupId", "GroupId"),
    groupName: getValue(item, "groupName", "GroupName", "Unknown group"),
    choice,
    reason: getValue(item, "voteReason", "VoteReason", ""),
    votedOn: getValue(item, "votedOn", "VotedOn"),
    createdOn: getValue(item, "campaignCreatedOn", "CampaignCreatedOn"),
    expiresOn,
    voteUp,
    voteDown,
    totalVotes,
    eligibleMembers,
    percentToApprove: toNumber(getValue(item, "percentToApprove", "PercentToApprove")),
    requiredApprovalVotes,
    isApproved,
    approvalPercent,
    participationPercent,
    resultLabel,
  };
};

const MetricTile = ({ icon, label, value, detail }) => (
  <Card className="border-white/10 bg-slate-950/55 shadow-[0_18px_55px_rgba(0,0,0,0.32)]">
    <CardContent className="flex min-h-[112px] items-center gap-4 p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-cyan-400/10 text-cyan-200">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-50">{value}</p>
        {detail && <p className="mt-1 text-sm text-slate-300">{detail}</p>}
      </div>
    </CardContent>
  </Card>
);

const FilterButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
      active
        ? "border-cyan-300 bg-cyan-300 text-slate-950"
        : "border-white/15 bg-slate-950/60 text-slate-200 hover:border-cyan-200/70 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const VoteHistoryCard = ({ vote }) => {
  const yesPercent = vote.totalVotes > 0 ? Math.round((vote.voteUp / vote.totalVotes) * 100) : 0;
  const noPercent = vote.totalVotes > 0 ? 100 - yesPercent : 0;
  const approvalWidth = Math.min(Math.max(vote.approvalPercent, 0), 100);
  const userSupported = vote.choice === true;

  return (
    <article className="rounded-lg border border-white/10 bg-slate-950/60 p-4 shadow-[0_16px_45px_rgba(0,0,0,0.28)]">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">
              {vote.groupName}
            </span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${vote.isApproved ? "bg-emerald-400/15 text-emerald-200" : vote.resultLabel === "In progress" ? "bg-amber-300/15 text-amber-200" : "bg-rose-300/15 text-rose-200"}`}>
              {vote.resultLabel}
            </span>
          </div>
          <h2 className="mt-3 text-xl font-bold leading-tight text-slate-50">{vote.title}</h2>
          {vote.proposal && <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-300">{vote.proposal}</p>}
        </div>
        <div className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold ${userSupported ? "border-emerald-300/30 bg-emerald-300/10 text-emerald-100" : "border-rose-300/30 bg-rose-300/10 text-rose-100"}`}>
          {userSupported ? <ThumbsUp className="h-4 w-4" /> : <ThumbsDown className="h-4 w-4" />}
          You voted {userSupported ? "yes" : "no"}
        </div>
      </div>

      {vote.reason && (
        <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm leading-6 text-slate-200">
          <span className="font-semibold text-slate-100">Your reason: </span>
          {vote.reason}
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
            <span>Campaign result</span>
            <span>{vote.voteUp} yes / {vote.voteDown} no</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300" style={{ width: `${yesPercent}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-400">
            <span>{yesPercent}% yes</span>
            <span>{noPercent}% no</span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between text-sm text-slate-300">
            <span>Approval threshold</span>
            <span>{vote.requiredApprovalVotes} of {vote.eligibleMembers}</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-gradient-to-r from-amber-200 to-emerald-300" style={{ width: `${approvalWidth}%` }} />
          </div>
          <div className="mt-2 text-xs text-slate-400">
            {vote.approvalPercent}% of eligible members approved; {vote.participationPercent}% participated
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-slate-300 md:grid-cols-4">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Voted</p>
          <p className="mt-1 font-semibold text-slate-100">{formatDate(vote.votedOn)}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Type</p>
          <p className="mt-1 font-semibold text-slate-100">{vote.campaignType || "Campaign"}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Total votes</p>
          <p className="mt-1 font-semibold text-slate-100">{vote.totalVotes}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Expires</p>
          <p className="mt-1 font-semibold text-slate-100">{formatDate(vote.expiresOn)}</p>
        </div>
      </div>
    </article>
  );
};

const VotingHistoryPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;
  const testDataToolsEnabled = isFeatureEnabled(FEATURE_FLAGS.testDataTools);
  const [votes, setVotes] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [seedStatus, setSeedStatus] = useState(null);

  const fetchVotingHistory = React.useCallback(async () => {
    setStatus("loading");
    setError("");

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      });

      const response = await fetch(`${apiUrl}/api/Votes/user/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to load voting history (${response.status})`);
      }

      const data = await response.json();
      setVotes(Array.isArray(data) ? data.map(normalizeVote) : []);
      setStatus("ready");
    } catch (err) {
      setError(err.message || "Unable to load voting history.");
      setStatus("error");
    }
  }, [apiUrl, getAccessTokenSilently]);

  useEffect(() => {
    let isMounted = true;

    fetchVotingHistory().catch(() => {
      if (isMounted) setStatus("error");
    });

    return () => {
      isMounted = false;
    };
  }, [fetchVotingHistory]);

  const seedVoteHistory = async () => {
    if (!apiUrl || seedStatus === "loading") return;

    setSeedStatus("loading");
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      });

      const response = await fetch(`${apiUrl}/api/DashboardTestData/vote-history`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to create voting test data (${response.status})`);
      }

      setSeedStatus("success");
      await fetchVotingHistory();
    } catch (err) {
      console.error("Error creating voting test data:", err);
      setSeedStatus("error");
    }
  };

  const metrics = useMemo(() => {
    const supported = votes.filter((vote) => vote.choice === true).length;
    const opposed = votes.filter((vote) => vote.choice === false).length;
    const approved = votes.filter((vote) => vote.isApproved).length;
    const avgParticipation = votes.length
      ? Math.round(votes.reduce((total, vote) => total + vote.participationPercent, 0) / votes.length)
      : 0;
    const mostRecent = votes[0]?.votedOn;

    return {
      total: votes.length,
      supported,
      opposed,
      approved,
      avgParticipation,
      mostRecent,
    };
  }, [votes]);

  const filteredVotes = useMemo(() => {
    if (filter === "supported") return votes.filter((vote) => vote.choice === true);
    if (filter === "opposed") return votes.filter((vote) => vote.choice === false);
    if (filter === "approved") return votes.filter((vote) => vote.isApproved);
    if (filter === "pending") return votes.filter((vote) => vote.resultLabel === "In progress");
    return votes;
  }, [filter, votes]);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_18%_0%,rgba(34,211,238,0.12),transparent_28%),radial-gradient(circle_at_88%_12%,rgba(251,191,36,0.12),transparent_36%),linear-gradient(180deg,#070b16_0%,#101522_50%,#090d16_100%)] px-4 pb-10 pt-6 text-slate-100">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button asChild className="w-fit border border-white/15 bg-slate-950/60 text-slate-100 hover:bg-slate-900">
            <Link to="/dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Dashboard
            </Link>
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Clock3 className="h-4 w-4" />
            Last vote: {formatDate(metrics.mostRecent)}
          </div>
        </div>

        <section className="mb-8 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/55 shadow-[0_25px_90px_rgba(0,0,0,0.42)]">
          <div className="relative px-5 py-7 md:px-8">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,rgba(45,212,191,0.16),transparent_34%),radial-gradient(circle_at_86%_8%,rgba(250,204,21,0.18),transparent_42%)]" />
            <div className="relative max-w-3xl">
              <p className="mb-2 inline-flex rounded-full border border-cyan-200/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
                Civic Ledger
              </p>
              <h1 className="text-3xl font-bold md:text-4xl">Voting history</h1>
              <p className="mt-3 text-sm leading-6 text-slate-300 md:text-base">
                Review every campaign vote you have cast, how your choices landed, and the results each group has reached so far.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile icon={<Vote className="h-6 w-6" />} label="Votes cast" value={metrics.total} detail={`${metrics.supported} yes, ${metrics.opposed} no`} />
          <MetricTile icon={<Trophy className="h-6 w-6" />} label="Approved outcomes" value={metrics.approved} detail="Campaigns that reached threshold" />
          <MetricTile icon={<Gauge className="h-6 w-6" />} label="Avg. participation" value={`${metrics.avgParticipation}%`} detail="Across campaigns you joined" />
          <MetricTile icon={<BarChart3 className="h-6 w-6" />} label="Result visibility" value={votes.length ? "Live" : "No votes"} detail="Uses current campaign totals" />
        </section>

        <section className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-50">Campaign votes</h2>
            <p className="mt-1 text-sm text-slate-400">{filteredVotes.length} shown from {votes.length} total</p>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            {testDataToolsEnabled && (
              <button
                type="button"
                onClick={seedVoteHistory}
                disabled={seedStatus === "loading"}
                className="min-h-11 rounded-lg border border-cyan-300/30 bg-cyan-400/10 px-3 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {seedStatus === "loading" ? "Creating test data..." : "Create voting test data"}
              </button>
            )}
            <div className="flex flex-wrap gap-2 md:justify-end">
              <span className="flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/60 px-3 py-2 text-sm font-semibold text-slate-300">
                <Filter className="h-4 w-4" />
                Filter
              </span>
              <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>All</FilterButton>
              <FilterButton active={filter === "supported"} onClick={() => setFilter("supported")}>Yes</FilterButton>
              <FilterButton active={filter === "opposed"} onClick={() => setFilter("opposed")}>No</FilterButton>
              <FilterButton active={filter === "approved"} onClick={() => setFilter("approved")}>Approved</FilterButton>
              <FilterButton active={filter === "pending"} onClick={() => setFilter("pending")}>In progress</FilterButton>
            </div>
          </div>
        </section>

        {testDataToolsEnabled && seedStatus === "success" && (
          <p className="mb-5 rounded-lg border border-emerald-300/20 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-100">
            Voting test data created from the local database.
          </p>
        )}
        {testDataToolsEnabled && seedStatus === "error" && (
          <p className="mb-5 rounded-lg border border-red-300/20 bg-red-400/10 px-3 py-2 text-sm text-red-100">
            Could not create voting test data. Make sure API TestApis and client test data tools are enabled locally.
          </p>
        )}

        {status === "loading" && (
          <div className="rounded-lg border border-white/10 bg-slate-950/60 p-6 text-slate-200">Loading voting history...</div>
        )}

        {status === "error" && (
          <div className="rounded-lg border border-rose-300/25 bg-rose-400/10 p-6 text-rose-100">{error}</div>
        )}

        {status === "ready" && filteredVotes.length === 0 && (
          <div className="rounded-lg border border-white/10 bg-slate-950/60 p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-cyan-200" />
            <h2 className="mt-3 text-xl font-bold text-slate-50">No votes match this view</h2>
            <p className="mt-2 text-sm text-slate-400">Try a different filter or cast a campaign vote to start building your history.</p>
          </div>
        )}

        {status === "ready" && filteredVotes.length > 0 && (
          <div className="space-y-4">
            {filteredVotes.map((vote) => (
              <VoteHistoryCard key={`${vote.campaignId}-${vote.voteId}`} vote={vote} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default VotingHistoryPage;
